import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaClock,
  FaMapMarkerAlt,
  FaParking,
  FaRoute,
  FaShieldAlt,
  FaStar,
  FaStore,
} from "react-icons/fa";
import NavBar from "../components/nav";
import Footer from "./footer";
import VisitorTips from "./VisitorTips";
import "../css/attractionDescription.css";
import { notifyError, notifySuccess } from "../utils/notify";

const AttractionDescription = () => {
  const navigate = useNavigate();
  const { state = {} } = useLocation();
  const [selectedImage, setSelectedImage] = useState("");

  const {
    title = "Attraction",
    description = "No description available.",
    location: attractionLocation = "Location not specified",
    rating = 0,
    shops = [],
    openingHours = "09:00 to 18:00",
    category = "Attraction",
  } = state;

  const gallery = useMemo(() => {
    const images = Array.isArray(state.gallery) ? state.gallery.filter(Boolean) : [];
    return [...new Set([state.image, ...images].filter(Boolean))];
  }, [state.gallery, state.image]);

  const safeRating = Math.min(5, Math.max(0, Number(rating) || 0));

  useEffect(() => {
    setSelectedImage(gallery[0] || "");
  }, [gallery]);

  const openDirections = () => {
    const destinationValue =
      attractionLocation && attractionLocation !== "Location not specified"
        ? attractionLocation
        : title;

    if (!destinationValue || destinationValue === "Attraction") {
      notifyError("Map directions are not available for this attraction yet.");
      return;
    }

    const destination = encodeURIComponent(destinationValue);
    notifySuccess("Opening Maps for directions.");
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${destination}`, "_blank");
  };

  if (!state.title) {
    return (
      <div className="attraction-description-page">
        <NavBar />
        <main className="attraction-detail-container">
          <button className="attraction-back-button" onClick={() => navigate(-1)} type="button">
            <FaArrowLeft />
            Back
          </button>
          <section className="attraction-empty-state">
            <h1>Attraction details unavailable</h1>
            <p>Please return to the attractions list and choose a place to visit.</p>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="attraction-description-page">
      <NavBar />
      <main className="attraction-detail-container">
        <header className="attraction-title-row">
          <button
            className="attraction-back-button icon-only"
            onClick={() => navigate(-1)}
            type="button"
          >
            <FaArrowLeft />
          </button>
          <div>
            <p className="attraction-eyebrow">
              <FaStore />
              {category}
            </p>
            <h1>{title}</h1>
            <div className="attraction-meta-row">
              <span>
                <FaMapMarkerAlt />
                {attractionLocation}
              </span>
              <span>
                <FaStar />
                {safeRating}/5 visitor experience
              </span>
            </div>
          </div>
        </header>

        <section className="attraction-hero-grid">
          <div className="attraction-main-image">
            {selectedImage ? (
              <img src={selectedImage} alt={title} />
            ) : (
              <div className="attraction-image-placeholder">
                <FaStore />
                <span>{category}</span>
              </div>
            )}
            <div className="attraction-hero-overlay">
              <span>{openingHours}</span>
              <strong>{shops.length} shops listed</strong>
            </div>
          </div>

          <div className="attraction-thumbnail-grid">
            {gallery.slice(0, 4).map((src, index) => (
              <button
                key={src}
                type="button"
                className={`attraction-thumbnail ${
                  selectedImage === src ? "active" : ""
                }`}
                onClick={() => setSelectedImage(src)}
              >
                <img src={src} alt={`${title} view ${index + 1}`} />
              </button>
            ))}
            {!gallery.length && (
              <div className="attraction-thumbnail empty">
                <FaStore />
              </div>
            )}
          </div>
        </section>

        <section className="attraction-content-grid">
          <div className="attraction-main-content">
            <div className="attraction-section attraction-review-panel">
              <span className="attraction-icon-badge primary">
                <FaStore />
              </span>
              <div>
                <p>Visitor Experience</p>
                <div className="attraction-stars">
                  <strong>{safeRating}</strong>
                  {Array.from({ length: 5 }, (_, index) => (
                    <FaStar
                      key={index}
                      className={index < safeRating ? "filled" : ""}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="attraction-section">
              <h2>About This Attraction</h2>
              <p>{description}</p>
            </div>

            <div className="attraction-section">
              <h2>Key Information</h2>
              <div className="attraction-info-grid">
                <div className="attraction-info-item cyan">
                  <span>
                    <FaMapMarkerAlt />
                  </span>
                  <div>
                    <strong>Address</strong>
                    <p>{attractionLocation}</p>
                  </div>
                </div>
                <div className="attraction-info-item amber">
                  <span>
                    <FaClock />
                  </span>
                  <div>
                    <strong>Opening Hours</strong>
                    <p>{openingHours}</p>
                  </div>
                </div>
                <div className="attraction-info-item green">
                  <span>
                    <FaStore />
                  </span>
                  <div>
                    <strong>Shops & Boutiques</strong>
                    <p>{shops.length} unique shops available</p>
                  </div>
                </div>
                <div className="attraction-info-item blue">
                  <span>
                    <FaParking />
                  </span>
                  <div>
                    <strong>Parking</strong>
                    <p>Ample parking space available</p>
                  </div>
                </div>
                <div className="attraction-info-item rose">
                  <span>
                    <FaShieldAlt />
                  </span>
                  <div>
                    <strong>Safety</strong>
                    <p>24/7 security surveillance</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="attraction-section">
              <h2>Featured Shops</h2>
              {shops.length > 0 ? (
                <div className="attraction-shop-grid">
                  {shops.map((shop, index) => (
                    <span key={`${shop.shopName}-${index}`}>
                      <FaStore />
                      {shop.shopName}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="attraction-muted">No shop information available yet.</p>
              )}
            </div>
          </div>

          <aside className="attraction-side-panel">
            <div className="attraction-directions-card">
              <span>
                <FaRoute />
              </span>
              <h2>Get Directions</h2>
              <p>Open the route in Maps and plan your visit with confidence.</p>
              <button type="button" onClick={openDirections}>
                <FaMapMarkerAlt />
                Open In Maps
              </button>
            </div>
          </aside>
        </section>
      </main>
      <section className="detail-extra-section">
        <VisitorTips title={title} category="Attraction" />
      </section>
      <Footer />
    </div>
  );
};

export default AttractionDescription;
